import React, { FunctionComponent } from "react"
import { NewsFeedItem } from "../../data/model/whats-new-model"

interface Props {
    item: NewsFeedItem
}

const CONTENT_TYPE_LABELS: Record<string, string> = {
    "blog":         "Blog",
    "release-note": "Release Note",
    "webinar":      "Webinar",
    "event":        "Event",
    "general":      "General",
}

function formatDate(iso: string): string {
    const d = new Date(iso)
    // An unparseable date string yields an Invalid Date, whose toLocaleDateString returns
    // the literal "Invalid Date" (it does not throw). Guard so bad dates render as empty.
    if (isNaN(d.getTime())) return ""
    try {
        return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    } catch {
        return ""
    }
}

// Only well-formed http(s) URLs are treated as clickable; anything else (empty or malformed
// link) renders the title as plain, non-clickable text per UI Spec §5.4.
function isValidLink(link: string): boolean {
    try {
        const u = new URL(link)
        return u.protocol === "http:" || u.protocol === "https:"
    } catch {
        return false
    }
}

export const WhatsNewFeedItem: FunctionComponent<Props> = ({ item }) => {
    const tagClass = `wn-item__tag wn-item__tag--${item.contentType}`
    const label = CONTENT_TYPE_LABELS[item.contentType] ?? item.contentType
    const linkable = isValidLink(item.link)

    return (
        <div className="wn-item">
            {item.pinned && <span className="wn-item__pinned-label">PINNED</span>}
            {item.imageUrl && (
                <img className="wn-item__image" src={item.imageUrl} alt="" />
            )}
            <div className="wn-item__body">
                <div className="wn-item__meta">
                    <span className={tagClass}>{label}</span>
                    {item.tags?.map((tag, i) => (
                        <span key={`${tag}-${i}`} className="wn-item__chip">{tag}</span>
                    ))}
                    <span className="wn-item__date">{formatDate(item.publishedAt)}</span>
                </div>
                <div className="wn-item__title">
                    {linkable ? (
                        <a
                            href={item.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`${item.title} (opens in new tab)`}
                        >
                            <span className="wn-item__title-text">{item.title}</span>
                            <span className="wn-item__ext" aria-hidden="true">↗</span>
                        </a>
                    ) : (
                        <span className="wn-item__title-text">{item.title}</span>
                    )}
                </div>
                {item.description && (
                    <div className="wn-item__description">{item.description}</div>
                )}
            </div>
        </div>
    )
}
